import {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import DeleteAccountModal from './DeleteAccountModal';
import {useToast} from './Toast';
import {isAuthError, fetchProfile, updateProfile, changePassword} from '../api';

export default function AccountSettingsPage({username, onDeleteAccount, onBack}) {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const showToast = useToast();

    // Profile state
    const [displayName, setDisplayName] = useState('');
    const [displayNameDraft, setDisplayNameDraft] = useState('');
    const [profileLoading, setProfileLoading] = useState(true);
    const [profileSaving, setProfileSaving] = useState(false);

    // Password state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [passwordSaving, setPasswordSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchProfile().then(data => {
            if (cancelled) return;
            setDisplayName(data.displayName || '');
            setDisplayNameDraft(data.displayName || '');
            setProfileLoading(false);
        }).catch(err => {
            if (cancelled) return;
            if (!isAuthError(err)) showToast(err.message);
            setProfileLoading(false);
        });
        return () => { cancelled = true; };
    }, [showToast]);

    const profileDirty = displayNameDraft !== displayName;

    const handleSaveProfile = useCallback(async () => {
        setProfileSaving(true);
        try {
            const data = await updateProfile(displayNameDraft.trim());
            setDisplayName(data.displayName || '');
            setDisplayNameDraft(data.displayName || '');
            showToast('Profile updated');
        } catch (err) {
            if (!isAuthError(err)) showToast(err.message);
        } finally {
            setProfileSaving(false);
        }
    }, [displayNameDraft, showToast]);

    const handleChangePassword = useCallback(async (e) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');

        if (newPassword.length < 8) {
            setPasswordError('New password must be at least 8 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('Passwords do not match');
            return;
        }

        setPasswordSaving(true);
        try {
            await changePassword(currentPassword, newPassword);
            setPasswordSuccess('Password changed successfully');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            if (!isAuthError(err)) setPasswordError(err.message);
        } finally {
            setPasswordSaving(false);
        }
    }, [currentPassword, newPassword, confirmPassword]);

    const handleDelete = useCallback(async () => {
        try {
            await onDeleteAccount();
        } catch (err) {
            setShowDeleteModal(false);
            if (!isAuthError(err)) showToast(err.message);
        }
    }, [onDeleteAccount, showToast]);

    return (
        <div className="settings-page">
            <div className="settings-container">
                <div className="settings-header">
                    <button className="settings-back" onClick={onBack}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M9 2L4 7L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Back
                    </button>
                    <h1 className="settings-title">Account settings</h1>
                </div>

                <div className="settings-section">
                    <h2 className="settings-section-title">Profile</h2>
                    <div className="settings-row">
                        <span className="settings-row-label">Email</span>
                        <span className="settings-row-value">{username}</span>
                    </div>
                    <div className="settings-row settings-row--border">
                        <label className="settings-row-label" htmlFor="display-name">Display name</label>
                        <div className="settings-field-group">
                            <input
                                id="display-name"
                                className="settings-input"
                                type="text"
                                value={profileLoading ? '' : displayNameDraft}
                                onChange={e => setDisplayNameDraft(e.target.value)}
                                placeholder="Your name"
                                maxLength={100}
                                disabled={profileLoading}
                            />
                            {profileDirty && (
                                <button
                                    className="settings-save-btn"
                                    onClick={handleSaveProfile}
                                    disabled={profileSaving}
                                >
                                    {profileSaving ? 'Saving...' : 'Save'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="settings-section">
                    <h2 className="settings-section-title">Security</h2>
                    <form className="settings-form" onSubmit={handleChangePassword}>
                        <div className="settings-form-row">
                            <label className="settings-row-label" htmlFor="current-password">Current password</label>
                            <input
                                id="current-password"
                                className="settings-input"
                                type="password"
                                value={currentPassword}
                                onChange={e => { setCurrentPassword(e.target.value); setPasswordError(''); setPasswordSuccess(''); }}
                                autoComplete="current-password"
                            />
                        </div>
                        <div className="settings-form-row">
                            <label className="settings-row-label" htmlFor="new-password">New password</label>
                            <input
                                id="new-password"
                                className="settings-input"
                                type="password"
                                value={newPassword}
                                onChange={e => { setNewPassword(e.target.value); setPasswordError(''); setPasswordSuccess(''); }}
                                autoComplete="new-password"
                            />
                        </div>
                        <div className="settings-form-row">
                            <label className="settings-row-label" htmlFor="confirm-password">Confirm new password</label>
                            <input
                                id="confirm-password"
                                className="settings-input"
                                type="password"
                                value={confirmPassword}
                                onChange={e => { setConfirmPassword(e.target.value); setPasswordError(''); setPasswordSuccess(''); }}
                                autoComplete="new-password"
                            />
                        </div>
                        {passwordError && <p className="settings-form-error">{passwordError}</p>}
                        {passwordSuccess && <p className="settings-form-success">{passwordSuccess}</p>}
                        <div className="settings-form-actions">
                            <button
                                type="submit"
                                className="settings-save-btn"
                                disabled={passwordSaving || !currentPassword || !newPassword || !confirmPassword}
                            >
                                {passwordSaving ? 'Changing...' : 'Change password'}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="settings-section settings-section--danger">
                    <h2 className="settings-section-title settings-section-title--danger">Danger zone</h2>
                    <div className="settings-danger-row">
                        <div className="settings-danger-info">
                            <span className="settings-danger-label">Delete account</span>
                            <span className="settings-danger-desc">Permanently remove your account and all checklists. This cannot be undone.</span>
                        </div>
                        <button className="settings-danger-button" onClick={() => setShowDeleteModal(true)}>
                            Delete account
                        </button>
                    </div>
                </div>
            </div>

            {showDeleteModal && (
                <DeleteAccountModal
                    onConfirm={handleDelete}
                    onCancel={() => setShowDeleteModal(false)}
                />
            )}
        </div>
    );
}

AccountSettingsPage.propTypes = {
    username: PropTypes.string.isRequired,
    onDeleteAccount: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
};
